import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Upload, CheckCircle, XCircle, Loader2, Shield } from "lucide-react";

interface CertificateUploadProps {
  hasCertificate: boolean;
  certificateExpiry?: string;
  onUpload: (certificateP12: string, password: string) => Promise<void>;
  isUploading?: boolean;
}

/**
 * Certificate upload component with drag-drop support.
 */
export function CertificateUpload({
  hasCertificate,
  certificateExpiry,
  onUpload,
  isUploading,
}: CertificateUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.match(/\.(p12|pfx)$/i)) {
        setError("Por favor seleccione un archivo .p12 o .pfx");
        return;
      }
      setFile(droppedFile);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        if (!selectedFile.name.match(/\.(p12|pfx)$/i)) {
          setError("Por favor seleccione un archivo .p12 o .pfx");
          return;
        }
        setFile(selectedFile);
      }
    },
    [],
  );

  const handleUpload = async () => {
    if (!file || !password) {
      setError("Seleccione un archivo y proporcione la contraseña");
      return;
    }

    setError(null);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(",")[1];
        if (base64) {
          await onUpload(base64, password);
          setFile(null);
          setPassword("");
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al cargar certificado",
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Estado del Certificado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            {hasCertificate ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium">Certificado configurado</p>
                  {certificateExpiry && (
                    <p className="text-sm text-muted-foreground">
                      Vence: {certificateExpiry}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium">Sin certificado</p>
                  <p className="text-sm text-muted-foreground">
                    Suba su certificado digital para continuar
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>Cargar Certificado</CardTitle>
          <CardDescription>
            Suba su certificado digital (.p12 o .pfx) emitido por una entidad
            certificadora acreditada
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"}
              ${file ? "border-green-500 bg-green-50" : ""}
            `}
          >
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle className="h-10 w-10 text-green-500" />
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
                  Cambiar archivo
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-10 w-10 text-muted-foreground" />
                <p className="font-medium">Arrastre su certificado aquí</p>
                <p className="text-sm text-muted-foreground">
                  o haga clic para seleccionar
                </p>
                <input
                  type="file"
                  accept=".p12,.pfx"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ position: "relative" }}
                />
                <Button variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".p12,.pfx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    Seleccionar archivo
                  </label>
                </Button>
              </div>
            )}
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <Label htmlFor="cert-password">Contraseña del certificado</Label>
            <Input
              id="cert-password"
              type="password"
              placeholder="Ingrese la contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {/* Error Message */}
          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Upload Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={!file || !password || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cargando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Cargar Certificado
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
